import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PropertyCard from '@/components/PropertyCard';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Search, SlidersHorizontal, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PropertiesPage = () => {
  const { type } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectors, setSectors] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    property_type: type || searchParams.get('type') || '',
    sector: searchParams.get('sector') || '',
    listing_type: searchParams.get('listing') || '',
    search: searchParams.get('search') || '',
    min_price: '',
    max_price: '',
    min_area: '',
    max_area: '',
  });

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [sectorsRes, typesRes] = await Promise.all([
          axios.get(`${API}/sectors`),
          axios.get(`${API}/property-types`),
        ]);
        setSectors(sectorsRes.data.sectors);
        setPropertyTypes(typesRes.data.types);
      } catch (error) {
      }
    };
    fetchMetadata();
  }, []);

  useEffect(() => {
    // Update filters from URL params
    const typeFromUrl = type || searchParams.get('type') || '';
    const sectorFromUrl = searchParams.get('sector') || '';
    const listingFromUrl = searchParams.get('listing') || '';
    const searchFromUrl = searchParams.get('search') || '';
    
    setFilters(prev => ({
      ...prev,
      property_type: typeFromUrl,
      sector: sectorFromUrl,
      listing_type: listingFromUrl,
      search: searchFromUrl,
    }));
  }, [type, searchParams]);

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.search) params.append('search', filters.search);
        if (filters.property_type) params.append('property_type', filters.property_type);
        if (filters.sector) params.append('sector', filters.sector);
        if (filters.listing_type) params.append('listing_type', filters.listing_type);
        if (filters.min_price) params.append('min_price', filters.min_price);
        if (filters.max_price) params.append('max_price', filters.max_price);
        if (filters.min_area) params.append('min_area', filters.min_area);
        if (filters.max_area) params.append('max_area', filters.max_area);

        const response = await axios.get(`${API}/properties?${params.toString()}`);
        setProperties(response.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      property_type: '',
      listing_type: '',
      sector: '',
      search: '',
      min_price: '',
      max_price: '',
      min_area: '',
      max_area: '',
    });
  };

  const getPageTitle = () => {
    if (filters.search) {
      return `Search: "${filters.search}"`;
    }
    if (type) {
      const typeObj = propertyTypes.find((t) => t.id === type);
      return typeObj ? typeObj.name : 'Properties';
    }
    return 'All Commercial Properties';
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== '');

  return (
    <div className="min-h-screen bg-white" data-testid="properties-page">
      <Header />

      {/* Page Header */}
      <section className="pt-32 pb-12 bg-gradient-to-r from-[#2a1e00] to-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#d4a828] text-xs uppercase tracking-[0.3em] mb-4 font-semibold">
            Premium Properties
          </p>
          <h1 className="text-3xl md:text-4xl font-bold text-white">
            {getPageTitle()}
          </h1>
          <p className="text-white/60 mt-4 max-w-2xl">
            Discover premium commercial real estate opportunities in Gurgaon&apos;s most
            sought-after locations.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-white border-b border-[rgba(201,168,76,0.2)] sticky top-20 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search Input Row */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8a7a50]" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by project name, developer, or location..."
                className="w-full pl-12 pr-4 py-3 border border-[rgba(201,168,76,0.2)] text-sm focus:border-[#c9a84c] focus:outline-none rounded-lg"
                data-testid="search-input"
              />
              {filters.search && (
                <button
                  onClick={() => handleFilterChange('search', '')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8a7a50] hover:text-[#2a1e00]"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Desktop Filters */}
            <div className="hidden lg:flex items-center gap-4 flex-wrap">
              {/* Listing Type Toggle */}
              <div className="flex bg-[#fdf8ee] p-1 rounded-lg">
                <button
                  onClick={() => handleFilterChange('listing_type', '')}
                  className={`px-4 py-2 text-xs font-medium uppercase tracking-wider transition-all rounded-md ${
                    !filters.listing_type
                      ? 'bg-[#c9a84c] text-white'
                      : 'text-[#6a5a30] hover:text-[#2a1e00]'
                  }`}
                  data-testid="filter-all-listing"
                >
                  All
                </button>
                <button
                  onClick={() => handleFilterChange('listing_type', 'sale')}
                  className={`px-4 py-2 text-xs font-medium uppercase tracking-wider transition-all rounded-md ${
                    filters.listing_type === 'sale'
                      ? 'bg-[#c9a84c] text-white'
                      : 'text-[#6a5a30] hover:text-[#2a1e00]'
                  }`}
                  data-testid="filter-buy"
                >
                  Buy
                </button>
                <button
                  onClick={() => handleFilterChange('listing_type', 'lease')}
                  className={`px-4 py-2 text-xs font-medium uppercase tracking-wider transition-all rounded-md ${
                    filters.listing_type === 'lease'
                      ? 'bg-[#c9a84c] text-white'
                      : 'text-[#6a5a30] hover:text-[#2a1e00]'
                  }`}
                  data-testid="filter-lease"
                >
                  Lease
                </button>
              </div>

              <Select
                value={filters.property_type || 'all'}
                onValueChange={(value) => handleFilterChange('property_type', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-48 rounded-lg" data-testid="filter-type">
                  <SelectValue placeholder="Property Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {propertyTypes.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.sector || 'all'}
                onValueChange={(value) => handleFilterChange('sector', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-48 rounded-lg" data-testid="filter-sector">
                  <SelectValue placeholder="Sector" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sectors</SelectItem>
                  {sectors.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Min Price"
                  value={filters.min_price}
                  onChange={(e) => handleFilterChange('min_price', e.target.value)}
                  className="w-32 rounded-lg"
                  data-testid="filter-min-price"
                />
                <span className="text-[#8a7a50]">-</span>
                <Input
                  type="number"
                  placeholder="Max Price"
                  value={filters.max_price}
                  onChange={(e) => handleFilterChange('max_price', e.target.value)}
                  className="w-32 rounded-lg"
                  data-testid="filter-max-price"
                />
              </div>

              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Min Sq.Ft."
                  value={filters.min_area}
                  onChange={(e) => handleFilterChange('min_area', e.target.value)}
                  className="w-28 rounded-lg"
                  data-testid="filter-min-area"
                />
                <span className="text-[#8a7a50]">-</span>
                <Input
                  type="number"
                  placeholder="Max Sq.Ft."
                  value={filters.max_area}
                  onChange={(e) => handleFilterChange('max_area', e.target.value)}
                  className="w-28 rounded-lg"
                  data-testid="filter-max-area"
                />
              </div>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-[#c9a84c]"
                  data-testid="clear-filters"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>

            {/* Mobile Filter Toggle */}
            <Button
              variant="outline"
              className="lg:hidden rounded-lg"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="mobile-filter-toggle"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <span className="ml-2 w-5 h-5 bg-[#c9a84c] text-white text-xs flex items-center justify-center rounded-full">
                  {Object.values(filters).filter((v) => v !== '').length}
                </span>
              )}
            </Button>

            {/* Results Count */}
            <p className="text-sm text-[#6a5a30]" data-testid="results-count">
              {properties.length} properties found
            </p>
          </div>

          {/* Mobile Filters Dropdown */}
          {showFilters && (
            <div className="lg:hidden mt-6 space-y-4 pb-4 border-t border-[rgba(201,168,76,0.2)] pt-4">
              {/* Mobile Listing Type Toggle */}
              <div className="flex bg-[#fdf8ee] p-1 rounded-lg">
                <button
                  onClick={() => handleFilterChange('listing_type', '')}
                  className={`flex-1 py-2 text-xs font-medium uppercase tracking-wider transition-all rounded-md ${
                    !filters.listing_type
                      ? 'bg-[#c9a84c] text-white'
                      : 'text-[#6a5a30]'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => handleFilterChange('listing_type', 'sale')}
                  className={`flex-1 py-2 text-xs font-medium uppercase tracking-wider transition-all rounded-md ${
                    filters.listing_type === 'sale'
                      ? 'bg-[#c9a84c] text-white'
                      : 'text-[#6a5a30]'
                  }`}
                >
                  Buy
                </button>
                <button
                  onClick={() => handleFilterChange('listing_type', 'lease')}
                  className={`flex-1 py-2 text-xs font-medium uppercase tracking-wider transition-all rounded-md ${
                    filters.listing_type === 'lease'
                      ? 'bg-[#c9a84c] text-white'
                      : 'text-[#6a5a30]'
                  }`}
                >
                  Lease
                </button>
              </div>

              <Select
                value={filters.property_type || 'all'}
                onValueChange={(value) => handleFilterChange('property_type', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-full rounded-lg">
                  <SelectValue placeholder="Property Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {propertyTypes.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.sector || 'all'}
                onValueChange={(value) => handleFilterChange('sector', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-full rounded-lg">
                  <SelectValue placeholder="Sector" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sectors</SelectItem>
                  {sectors.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  placeholder="Min Price"
                  value={filters.min_price}
                  onChange={(e) => handleFilterChange('min_price', e.target.value)}
                  className="rounded-lg"
                />
                <Input
                  type="number"
                  placeholder="Max Price"
                  value={filters.max_price}
                  onChange={(e) => handleFilterChange('max_price', e.target.value)}
                  className="rounded-lg"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  placeholder="Min Sq.Ft."
                  value={filters.min_area}
                  onChange={(e) => handleFilterChange('min_area', e.target.value)}
                  className="rounded-lg"
                />
                <Input
                  type="number"
                  placeholder="Max Sq.Ft."
                  value={filters.max_area}
                  onChange={(e) => handleFilterChange('max_area', e.target.value)}
                  className="rounded-lg"
                />
              </div>

              {hasActiveFilters && (
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="w-full rounded-lg"
                >
                  Clear All Filters
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Properties Grid */}
      <section className="py-12 bg-[#fdf9f0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white p-4 animate-pulse rounded-xl">
                  <div className="h-48 bg-[rgba(201,168,76,0.2)] mb-4 rounded-lg" />
                  <div className="h-4 bg-[rgba(201,168,76,0.2)] mb-2 w-3/4 rounded" />
                  <div className="h-4 bg-[rgba(201,168,76,0.2)] w-1/2 rounded" />
                </div>
              ))}
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-24">
              <Search className="w-16 h-16 mx-auto text-[rgba(201,168,76,0.2)] mb-6" />
              <h3 className="text-xl font-bold text-[#2a1e00] mb-2">
                No Properties Found
              </h3>
              <p className="text-[#6a5a30] mb-6">
                Try adjusting your filters to find more properties.
              </p>
              <Button onClick={clearFilters} className="btn-secondary">
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {properties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PropertiesPage;
